import { Link } from 'react-router';
import { usePrimaryColor } from '../../branding/usePrimaryColor';
import { formatCurrency } from '../../utils/currency';
import type { TopClientItem } from '../../types/dashboard';

interface TopClientsProps {
  clients: TopClientItem[];
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  onRetry?: () => void;
}

export function TopClients({
  clients,
  loading = false,
  error = false,
  forbidden = false,
  onRetry,
}: TopClientsProps) {
  const primaryColor = usePrimaryColor();

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Top clients</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Top clients</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load client data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && clients.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-24 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-3 pb-3 space-y-2.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="h-3 w-28 bg-canvas animate-pulse rounded" />
              <div className="h-3 w-16 bg-canvas animate-pulse rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (clients.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Top clients</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            Client rankings will appear after payments are confirmed.
          </p>
        </div>
      </div>
    );
  }

  const maxRevenue = Math.max(...clients.map((c) => c.totalRevenue), 1);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-semibold">Top clients</h3>
          <span className="text-[11.5px] text-ink-4">by lifetime value</span>
        </div>
      </div>

      {/* Client rows */}
      <div className="px-4 pt-3 pb-3 space-y-2">
        {clients.map((client, i) => {
          const barPct = Math.max((client.totalRevenue / maxRevenue) * 100, 3);
          const opacity = Math.max(1 - i * 0.08, 0.35);

          return (
            <div key={client.customerId} className="flex items-center gap-2">
              <span className="text-[10.5px] text-ink-4 num w-4 shrink-0 text-right">
                {i + 1}
              </span>
              {client.clientId ? (
                <Link
                  to={`/clients/${client.clientId}`}
                  className="text-[11.5px] text-ink-2 w-24 truncate shrink-0 hover:text-tide"
                  title={client.name}
                >
                  {client.name}
                </Link>
              ) : (
                <span className="text-[11.5px] text-ink-2 w-24 truncate shrink-0" title={client.name}>
                  {client.name}
                </span>
              )}
              <div className="flex-1 h-5 rounded overflow-hidden bg-canvas relative">
                <div
                  className="h-full rounded transition-all duration-300"
                  style={{
                    width: `${barPct}%`,
                    backgroundColor: primaryColor,
                    opacity,
                  }}
                />
              </div>
              <span className="text-[11.5px] font-medium num shrink-0 w-16 text-right">
                ${formatCurrency(client.totalRevenue)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
